import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Crown, Heart, Zap, ShieldCheck } from 'lucide-react';

const faqs = [
  {
    q: "What luxury amenities does Rudragarh offer?",
    a: "Rudragarh is a full-facility boutique resort. Amenities include a swimming pool, fully equipped gym, grand dining hall, bar room, traditional low-sitting hall, live and open kitchen, private sand dunes, natural pond and waterfall, open-air theatre, greenhouse, and photo booths across the property. Adventure experiences — ATV rides on the dunes, horse riding, camel safari, archery, football, and rain dance — are all available on-site. Every booking includes access to all facilities."
  },
  {
    q: "What are the premium room options at Rudragarh?",
    a: "The premium accommodations at Rudragarh are The Catacomb Suite (₹10,000/night, 4 guests) — the most unique room in the property, subterranean carved-cave cottages with natural climate control — and The Grand Quad (₹7,000/night, 4 guests), the grandest room on the property with a king bed, steps from the bar, pool and garden. For those wanting more space, The Desert Riad Suite (₹18,000/night, 4-6 guests) offers a spacious riad-inspired flat suite with a full kitchen and premium comforts. All premium rooms include minibar and full resort access."
  },
  {
    q: "Is Rudragarh suitable for a luxury honeymoon in Jaisalmer?",
    a: "Rudragarh is well suited for a honeymoon. The Catacomb Suite offers unmatched privacy and atmosphere — carved rock, natural climate control, and the Thar Desert outside. The boutique scale of the property means you're never in a crowded resort corridor. The natural pond and waterfall, private sand dunes, and cultural evenings create a romantic setting that peak-season Jaisalmer hotels can't replicate. Contact us for honeymooner arrangements — we can customise the experience."
  }
];

const luxuryPoints = [
  { icon: <Crown size={28} />, title: "Boutique Scale", desc: "Eight distinct room types, 22 total units. You get personal attention — not a room number in a 200-key hotel." },
  { icon: <Zap size={28} />, title: "Private Dunes & Pool", desc: "Your own desert terrain plus a swimming pool — a combination almost impossible to find near Sam Sand Dunes." },
  { icon: <Heart size={28} />, title: "Romantic Atmosphere", desc: "Cave stays lit by lanterns, open-air theatre under stars, cultural evenings, and pond views — romance is built in." },
  { icon: <ShieldCheck size={28} />, title: "Everything Included", desc: "No à la carte activity fees, no surprise charges. 18 facilities, all activities, one booking. That's the Rudragarh model." }
];

const rooms = [
  { slug: "catacomb-suite", name: "The Catacomb Suite", price: 10000, guests: 4, category: "unique", desc: "The crown of Rudragarh — carved rock walls, lantern light, and total desert immersion. Two double beds, private attached bathroom." },
  { slug: "grand-quad", name: "The Grand Quad", price: 7000, guests: 4, category: "modern", desc: "The property's largest room — king bed, modern finishes, and full resort access. One unit only." },
  { slug: "desert-riad-suite", name: "The Desert Riad Suite", price: 18000, guests: 6, category: "modern", desc: "Spacious riad-inspired flat suite with a full kitchen and room for 4–6 — every facility included." }
];

const LuxuryResort = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Luxury Resort in Jaisalmer | Rudragarh — Boutique Desert Retreat</title>
        <meta name="description" content="Rudragarh is a boutique luxury resort near Jaisalmer offering cave stays, king suites, private dunes, pool, gym, bar, open-air theatre, and personalised desert experiences." />
        <meta property="og:title" content="Luxury Resort in Jaisalmer | Rudragarh — Boutique Desert Retreat" />
        <meta property="og:description" content="Rudragarh — boutique luxury near Jaisalmer. Cave stays, king suites, private sand dunes, pool, gym, bar, open-air theatre, and personalised desert experiences." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/jaisalmer/luxury-resort" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-hut-night.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Luxury Resort Jaisalmer | Rudragarh Boutique Desert Retreat" />
        <meta name="twitter:description" content="Boutique luxury in the Jaisalmer desert — cave stays, king suites, private dunes, pool, and personalised hospitality at Rudragarh." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-hut-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/jaisalmer/luxury-resort" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-hut-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Boutique Luxury, Jaisalmer</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Boutique Luxury in the <span className="text-gold-500">Jaisalmer Desert</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Rudragarh is not a five-star hotel chain. It's something rarer — a boutique desert retreat with genuine character, private dunes, and hospitality that scales to the guest, not the spreadsheet.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">The Philosophy</span>
              <h2 className="text-3xl md:text-4xl font-heading font-medium">
                Luxury that <span className="text-gold-500">means something</span>
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                The word "luxury" has been stretched thin. It's applied to any hotel with marble in the bathroom. At Rudragarh, luxury has a different measure: the moment you realise the dunes outside are yours alone, the sound of folk music drifting over the open-air theatre, the particular silence of a carved cave room at midnight.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                The property is boutique by design — small enough that guests are known, large enough to offer a complete resort experience. The pool, the bar, the gym, the private sand dunes, the natural pond and waterfall — all available, all included. The cave stay and king suite sit at the top of the room tier, but every accommodation at Rudragarh has been built with intention.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-void-subtle border border-gold-500/20 p-8 space-y-5"
            >
              <h3 className="font-heading text-2xl mb-2">What's included at every price point</h3>
              {[
                "Swimming pool",
                "Fully equipped gym",
                "Grand dining hall & bar",
                "Private sand dunes",
                "Natural pond & waterfall",
                "ATV rides, horse riding, camel safari",
                "Archery, football, air gun range",
                "Cultural programs & open-air theatre",
                "Rain dance & kids' play zone",
                "Cycling, jogging, trekking tracks",
                "Greenhouse & photo booths"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 pb-3 border-b border-gold-500/10 last:border-0 last:pb-0">
                  <div className="w-1.5 h-1.5 bg-gold-500 rounded-full flex-shrink-0" />
                  <span className="text-foreground/70 text-sm">{item}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Luxury Points */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">The Difference</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Why Rudragarh is <span className="text-gold-500">Different</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {luxuryPoints.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{p.icon}</div>
                <h3 className="font-heading text-xl">{p.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Premium Stays</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              The <span className="text-gold-500">Top Rooms</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              Three rooms at the top of the Rudragarh tier — each offering something distinct and worth the premium.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group relative"
              >
                {room.slug === 'cave-stay' && (
                  <div className="absolute top-0 right-0 bg-gold-500 text-black text-xs px-3 py-1 uppercase tracking-widest">
                    Most Unique
                  </div>
                )}
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3 pt-8">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All 6 Rooms</Link>
          </div>
        </div>
      </section>

      {/* For Honeymooners */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto container-padding text-center space-y-6">
          <span className="overline">Honeymoons & Special Occasions</span>
          <h2 className="text-3xl md:text-4xl font-heading font-medium">
            Jaisalmer's most <span className="text-gold-500">romantic desert stay</span>
          </h2>
          <p className="text-foreground/70 leading-relaxed max-w-2xl mx-auto">
            The Catacomb Suite was not designed with honeymooners in mind — but they keep coming back. The combination of carved rock walls, lantern light, private desert silence, and the backdrop of the Thar produces something no decorator could plan. If you're planning a honeymoon or anniversary trip to Jaisalmer, the cave stay is the most talked-about room on the property. Reach out when booking and we'll arrange the rest.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link to="/stay/catacomb-suite" className="btn-primary">View Cave Stay</Link>
            <Link to="/contact" className="btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Luxury at <span className="text-gold-500">Rudragarh</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/82" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Book Your Retreat</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            Jaisalmer's Best <span className="text-gold-500">Desert Stay</span>
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Reserve your room at Rudragarh — boutique luxury in the Thar Desert, where every detail is designed to make the place unforgettable.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LuxuryResort;
